import { styled } from '@ori-ui/mui';
import type { FC } from 'react';
import { useState, useCallback, Suspense, lazy } from 'react';
import { FormattedMessage } from 'react-intl';

import Loader from './Loader';
import { MainButton } from './MainButton';
import { ShareButton } from './ShareButton';
import { translationKeys } from '~/models';
import { log, showSharePanel } from '~/utils';

interface FooterProps {
  isBasketSharingEnabled: boolean | undefined;
}

const Root = styled('div')(({ theme }) => ({
  background: 'linear-gradient(to top, #fff 20%, rgba(255,255,255,0.9) 30%)',
  bottom: 0,
  boxSizing: 'border-box',
  justifySelf: 'flex-end',
  padding: theme.spacing(3),
  position: 'absolute',
  width: '100%',
  zIndex: 2,
}));

interface SharedContent {
  message: string;
  url: string;
}

export const Footer: FC<FooterProps> = ({ isBasketSharingEnabled }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sharedContent, setSharedContent] = useState<Partial<SharedContent>>({});

  const SharingWrapper = lazy(async () => import('./SharingWrapper'));

  const closeHandler = useCallback(() => {
    // W/o postponing it, it didn't close the dialog in some cases for some reason.
    setTimeout(() => {
      setIsOpen(false);
      log('Closing sharing popup ...');
    });
  }, [setIsOpen]);

  const openHandler = useCallback(async () => {
    const { data } = await showSharePanel();

    setTimeout(() => {
      setIsOpen(true);
      setSharedContent(data);
    });
  }, [setIsOpen]);

  return (
    <Root>
      <MainButton>
        <FormattedMessage id={translationKeys.goToBasket} />
      </MainButton>
      {isBasketSharingEnabled ? (
        <>
          <ShareButton onClick={openHandler} />
          <Suspense fallback={<Loader />}>
            <SharingWrapper
              isOpen={isOpen}
              sharedContent={sharedContent}
              onClose={closeHandler}
            />
          </Suspense>
        </>
      ) : null}
    </Root>
  );
};
